import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  Loader2,
  Download,
  XCircle,
  Inbox,
  Eye,
  Pencil,
  SlidersHorizontal,
  Save,
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { generateVoucherPDF } from "@/lib/partner/voucher";
import { logOrderAction } from "@/lib/partner/audit";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";

export const Route = createFileRoute("/partner/")({
  component: OrdersPage,
});

interface OrderRow {
  order_id: string;
  status: string;
  start_date_time: string;
  passenger_name: string;
  passenger_phone: string;
  passenger_email: string | null;
  passenger_count: number;
  luggage_count: number;
  waiting_time_min: number;
  child_seats: number;
  pickup_label: string | null;
  dropoff_label: string | null;
  price_amount: number;
  price_currency: string;
  flight_number: string | null;
  meeting_sign_text: string | null;
  car_model_en: string | null;
  car_color_en: string | null;
  car_plate: string | null;
  driver_name: string | null;
  driver_phone: string | null;
  free_cancel_until: string | null;
}

type StatusFilter = "all" | "confirmed" | "ready" | "completed" | "cancelled";

const STATUS_META: Record<string, { label: string; cls: string }> = {
  pending: { label: "Pending", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  confirmed: { label: "Booking Confirmed", cls: "bg-blue-100 text-blue-800 border-blue-200" },
  ready: { label: "Ready for Execution", cls: "bg-indigo-100 text-indigo-800 border-indigo-200" },
  completed: { label: "Completed", cls: "bg-green-100 text-green-800 border-green-200" },
  cancelled: { label: "Cancelled", cls: "bg-red-100 text-red-800 border-red-200" },
};

function OrdersPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState<OrderRow[]>([]);
  const [busy, setBusy] = useState(true);
  const [actingId, setActingId] = useState<string | null>(null);
  const [filter, setFilter] = useState<StatusFilter>("all");

  const [detailsOrder, setDetailsOrder] = useState<OrderRow | null>(null);
  const [modifyOrder, setModifyOrder] = useState<OrderRow | null>(null);
  const [editOrder, setEditOrder] = useState<OrderRow | null>(null);

  const load = async () => {
    setBusy(true);
    const { data } = await supabase
      .from("bookings_orders")
      .select(
        "order_id, status, start_date_time, passenger_name, passenger_phone, passenger_email, passenger_count, luggage_count, waiting_time_min, child_seats, pickup_label, dropoff_label, price_amount, price_currency, flight_number, meeting_sign_text, car_model_en, car_color_en, car_plate, driver_name, driver_phone, free_cancel_until",
      )
      .order("start_date_time", { ascending: false })
      .limit(200);
    setRows((data as OrderRow[]) ?? []);
    setBusy(false);
  };

  useEffect(() => {
    if (user) void load();
  }, [user]);

  const counts = useMemo(() => {
    const c = { all: rows.length, confirmed: 0, ready: 0, completed: 0, cancelled: 0 };
    for (const r of rows) {
      if (r.status in c) (c as Record<string, number>)[r.status]++;
    }
    return c;
  }, [rows]);

  const filtered = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );

  const cancelOrder = async (orderId: string) => {
    if (!confirm(`Cancel order ${orderId}? This may incur a penalty per the cancellation policy.`))
      return;
    setActingId(orderId);
    const { error } = await supabase
      .from("bookings_orders")
      .update({ status: "cancelled", cancelled_at: new Date().toISOString() })
      .eq("order_id", orderId);
    setActingId(null);
    if (error) return toast.error(error.message);
    if (user) await logOrderAction(orderId, user.id, "cancel", { status: "cancelled" });
    toast.success("Order cancelled");
    void load();
  };

  const downloadVoucher = (o: OrderRow) => {
    generateVoucherPDF({
      order_id: o.order_id,
      status: o.status,
      passenger_name: o.passenger_name,
      passenger_phone: o.passenger_phone,
      passenger_email: o.passenger_email,
      passenger_count: o.passenger_count,
      start_date_time: o.start_date_time,
      flight_number: o.flight_number,
      meeting_sign_text: o.meeting_sign_text,
      pickup_label: o.pickup_label,
      dropoff_label: o.dropoff_label,
      car_model: o.car_model_en,
      driver_name: o.driver_name,
      driver_phone: o.driver_phone,
      car_plate: o.car_plate,
      price_amount: Number(o.price_amount),
      price_currency: o.price_currency,
    }).save(`voucher-${o.order_id}.pdf`);
  };

  const onSaved = async (
    orderId: string,
    action: string,
    changes: Record<string, unknown>,
  ) => {
    if (user) await logOrderAction(orderId, user.id, action, changes);
    toast.success("Saved · /status endpoint updated");
    setModifyOrder(null);
    setEditOrder(null);
    void load();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-2xl font-serif text-primary">Orders</h2>
          <p className="text-sm text-muted-foreground">
            {rows.length} {rows.length === 1 ? "order" : "orders"} · full lifecycle management
          </p>
        </div>
        <Tabs value={filter} onValueChange={(v) => setFilter(v as StatusFilter)}>
          <TabsList>
            <TabsTrigger value="all">All ({counts.all})</TabsTrigger>
            <TabsTrigger value="confirmed">Confirmed ({counts.confirmed})</TabsTrigger>
            <TabsTrigger value="ready">Ready ({counts.ready})</TabsTrigger>
            <TabsTrigger value="completed">Completed ({counts.completed})</TabsTrigger>
            <TabsTrigger value="cancelled">Cancelled ({counts.cancelled})</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      {busy ? (
        <div className="flex justify-center py-20">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="border border-dashed rounded-xl bg-card py-20 text-center">
          <Inbox className="h-12 w-12 mx-auto mb-4 text-muted-foreground/60" />
          <h3 className="text-lg font-medium">No orders yet</h3>
          <p className="text-sm text-muted-foreground mt-1">
            When partners book transfers, they'll appear here.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border bg-card shadow-sm">
          <table className="w-full text-sm">
            <thead className="bg-muted/40 text-left text-xs uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="px-4 py-3 font-semibold">Order ID</th>
                <th className="px-4 py-3 font-semibold">Date</th>
                <th className="px-4 py-3 font-semibold">Passenger</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Price</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((o) => (
                <tr key={o.order_id} className="border-t hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3">
                    <Link
                      to="/partner/orders/$orderId"
                      params={{ orderId: o.order_id }}
                      className="font-mono text-primary hover:underline"
                    >
                      {o.order_id}
                    </Link>
                  </td>
                  <td className="px-4 py-3 text-muted-foreground">
                    {new Date(o.start_date_time).toLocaleDateString()} ·{" "}
                    {new Date(o.start_date_time).toLocaleTimeString([], {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                    {o.flight_number && (
                      <div className="text-xs text-muted-foreground/80">Flight {o.flight_number}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{o.passenger_name}</div>
                    <div className="text-xs text-muted-foreground">
                      {o.passenger_count} pax · {o.luggage_count} bags
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={o.status} />
                  </td>
                  <td className="px-4 py-3 text-right font-medium">
                    {Number(o.price_amount).toFixed(2)} {o.price_currency}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1.5 flex-wrap">
                      <ActionBtn icon={Eye} label="Details" onClick={() => setDetailsOrder(o)} />
                      <ActionBtn
                        icon={SlidersHorizontal}
                        label="Modify"
                        onClick={() => setModifyOrder(o)}
                        disabled={o.status === "cancelled"}
                      />
                      <ActionBtn
                        icon={Pencil}
                        label="Edit"
                        onClick={() => setEditOrder(o)}
                        disabled={o.status === "cancelled"}
                      />
                      <ActionBtn
                        icon={Download}
                        label="Voucher"
                        onClick={() => downloadVoucher(o)}
                        disabled={o.status === "cancelled"}
                      />
                      <ActionBtn
                        icon={XCircle}
                        label="Cancel"
                        onClick={() => cancelOrder(o.order_id)}
                        danger
                        disabled={o.status === "cancelled" || actingId === o.order_id}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <DetailsDialog order={detailsOrder} onClose={() => setDetailsOrder(null)} />
      <ModifyDialog
        order={modifyOrder}
        onClose={() => setModifyOrder(null)}
        onSaved={onSaved}
      />
      <EditDialog order={editOrder} onClose={() => setEditOrder(null)} onSaved={onSaved} />
    </div>
  );
}

function ActionBtn({
  icon: Icon,
  label,
  onClick,
  danger,
  disabled,
}: {
  icon: typeof Eye;
  label: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 px-2 py-1.5 rounded-md text-xs border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        danger
          ? "border-destructive/30 text-destructive hover:bg-destructive/10"
          : "bg-background hover:bg-muted"
      }`}
    >
      <Icon className="h-3.5 w-3.5" /> {label}
    </button>
  );
}

function StatusBadge({ status }: { status: string }) {
  const meta = STATUS_META[status] ?? {
    label: status,
    cls: "bg-muted text-muted-foreground border-border",
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium border ${meta.cls}`}>
      {meta.label}
    </span>
  );
}

function DetailsDialog({ order, onClose }: { order: OrderRow | null; onClose: () => void }) {
  return (
    <Dialog open={!!order} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Order {order?.order_id}</DialogTitle>
          <DialogDescription>Full itinerary and requested services</DialogDescription>
        </DialogHeader>
        {order && (
          <div className="space-y-4 text-sm">
            <Row label="Pickup">
              {order.pickup_label || "—"} →{" "}
              <span className="font-medium">{order.dropoff_label || "—"}</span>
            </Row>
            <Row label="Date / time">
              {new Date(order.start_date_time).toLocaleString()}
            </Row>
            <div className="grid grid-cols-3 gap-3">
              <Stat label="Passengers" value={order.passenger_count} />
              <Stat label="Luggage" value={order.luggage_count} />
              <Stat label="Wait (min)" value={order.waiting_time_min} />
            </div>
            <Row label="Extras">
              {order.child_seats > 0 ? `${order.child_seats} × Child seat` : "None"}
            </Row>
            <Row label="Lead passenger">
              {order.passenger_name} · {order.passenger_phone}
              {order.passenger_email ? ` · ${order.passenger_email}` : ""}
            </Row>
            {order.flight_number && <Row label="Flight">{order.flight_number}</Row>}
            {order.meeting_sign_text && <Row label="Meeting sign">{order.meeting_sign_text}</Row>}
            <Row label="Vehicle">
              {order.car_model_en
                ? `${order.car_model_en}${order.car_color_en ? ` · ${order.car_color_en}` : ""}${order.car_plate ? ` · ${order.car_plate}` : ""}`
                : "Not assigned"}
            </Row>
            <Row label="Driver">
              {order.driver_name
                ? `${order.driver_name}${order.driver_phone ? ` · ${order.driver_phone}` : ""}`
                : "Not assigned"}
            </Row>
            <Row label="Price">
              <span className="font-semibold">
                {Number(order.price_amount).toFixed(2)} {order.price_currency}
              </span>
            </Row>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 border-b border-border/40 pb-2">
      <div className="w-32 shrink-0 text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="flex-1">{children}</div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold text-primary">{value}</div>
    </div>
  );
}

function ModifyDialog({
  order,
  onClose,
  onSaved,
}: {
  order: OrderRow | null;
  onClose: () => void;
  onSaved: (orderId: string, action: string, changes: Record<string, unknown>) => void;
}) {
  const [passengers, setPassengers] = useState(0);
  const [luggage, setLuggage] = useState(0);
  const [wait, setWait] = useState(0);
  const [seats, setSeats] = useState(0);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) {
      setPassengers(order.passenger_count);
      setLuggage(order.luggage_count);
      setWait(order.waiting_time_min);
      setSeats(order.child_seats);
    }
  }, [order]);

  if (!order) return null;

  const save = async () => {
    setSaving(true);
    const changes = {
      passenger_count: passengers,
      luggage_count: luggage,
      waiting_time_min: wait,
      child_seats: seats,
    };
    const { error } = await supabase
      .from("bookings_orders")
      .update(changes)
      .eq("order_id", order.order_id);
    setSaving(false);
    if (error) return toast.error(error.message);
    onSaved(order.order_id, "modify", changes);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modify Order {order.order_id}</DialogTitle>
          <DialogDescription>
            Adjust passenger count, luggage and waiting time
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Passengers</Label>
            <Input
              type="number"
              min={1}
              max={60}
              value={passengers}
              onChange={(e) => setPassengers(Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Luggage (bags)</Label>
            <Input
              type="number"
              min={0}
              max={60}
              value={luggage}
              onChange={(e) => setLuggage(Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Waiting time (min)</Label>
            <Input
              type="number"
              min={0}
              max={480}
              value={wait}
              onChange={(e) => setWait(Number(e.target.value))}
            />
          </div>
          <div>
            <Label>Child seats</Label>
            <Input
              type="number"
              min={0}
              max={10}
              value={seats}
              onChange={(e) => setSeats(Number(e.target.value))}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditDialog({
  order,
  onClose,
  onSaved,
}: {
  order: OrderRow | null;
  onClose: () => void;
  onSaved: (orderId: string, action: string, changes: Record<string, unknown>) => void;
}) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [flight, setFlight] = useState("");
  const [meeting, setMeeting] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (order) {
      setName(order.passenger_name);
      setPhone(order.passenger_phone);
      setEmail(order.passenger_email ?? "");
      setFlight(order.flight_number ?? "");
      setMeeting(order.meeting_sign_text ?? "");
    }
  }, [order]);

  if (!order) return null;

  const save = async () => {
    setSaving(true);
    const changes = {
      passenger_name: name,
      passenger_phone: phone,
      passenger_email: email || null,
      flight_number: flight || null,
      meeting_sign_text: meeting || null,
    };
    const { error } = await supabase
      .from("bookings_orders")
      .update(changes)
      .eq("order_id", order.order_id);
    setSaving(false);
    if (error) return toast.error(error.message);
    onSaved(order.order_id, "edit_contact", changes);
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Contact — {order.order_id}</DialogTitle>
          <DialogDescription>Update contact info and flight number</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Lead passenger name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Phone</Label>
              <Input value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div>
              <Label>Email</Label>
              <Input value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Flight number</Label>
              <Input value={flight} onChange={(e) => setFlight(e.target.value)} />
            </div>
            <div>
              <Label>Meeting sign</Label>
              <Input value={meeting} onChange={(e) => setMeeting(e.target.value)} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={save} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
